import { useEffect, useRef, useState } from "react";
import { ChatCircleDots, X, PaperPlaneTilt } from "@phosphor-icons/react";
import { API } from "../lib/api";

const sid = () => {
  let s = sessionStorage.getItem("greetly_chat_sid");
  if (!s) { s = crypto.randomUUID(); sessionStorage.setItem("greetly_chat_sid", s); }
  return s;
};

export default function ChatWidget({ slug, businessName, enabled = true }) {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([
    { role: "assistant", content: `Hi, I'm Mimi — powered by BuzzerAI, the AI receptionist for ${businessName || "BuzzerAI"}. How can I help you? I can add you to the waitlist, take a to-go order, or answer questions.` },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [msgs, open]);

  if (!enabled) return null;

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setMsgs((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setBusy(true);
    try {
      const res = await fetch(`${API}/chat/message`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sid(), message: text, business_slug: slug }),
      });
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let acc = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        acc += dec.decode(value, { stream: true });
        setMsgs((m) => { const copy = [...m]; copy[copy.length - 1] = { role: "assistant", content: acc }; return copy; });
      }
    } catch {
      setMsgs((m) => { const copy = [...m]; copy[copy.length - 1] = { role: "assistant", content: "Sorry, I had trouble responding. A team member will follow up." }; return copy; });
    }
    setBusy(false);
  };

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-[#1E3A2F] text-white shadow-lg flex items-center justify-center hover:-translate-y-0.5 transition-transform"
          data-testid="chat-open"
          aria-label="Open Mimi, the AI receptionist"
        >
          <ChatCircleDots size={24} weight="fill" />
        </button>
      )}
      {open && (
        <div className="fixed bottom-6 right-6 z-50 w-[360px] max-w-[calc(100vw-2rem)] paper-card flex flex-col overflow-hidden" style={{ height: 520 }}>
          <div className="bg-[#1E3A2F] text-white px-4 py-3 flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-white/60">Mimi · AI Receptionist</div>
              <div className="font-heading font-extrabold">{businessName}</div>
            </div>
            <button onClick={() => setOpen(false)} data-testid="chat-close" className="w-8 h-8 rounded-full hover:bg-white/10 flex items-center justify-center"><X size={16} color="#fff" /></button>
          </div>
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#F7F6F2]">
            {msgs.map((m, i) => (
              <div key={`chat-${m.ts || i}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm leading-relaxed ${m.role === "user" ? "bg-[#1E3A2F] text-white" : "bg-white border border-[#E1DFD5]"}`}>
                  {m.content || <span className="text-[#5C6B61]">…</span>}
                </div>
              </div>
            ))}
          </div>
          <div className="p-3 border-t border-[#E1DFD5] flex items-center gap-2">
            <input
              className="input-field !py-2" placeholder="Ask about waitlist, hours, or to-go…"
              value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") send(); }}
              data-testid="chat-input"
            />
            <button onClick={send} disabled={busy} className="w-10 h-10 rounded-full bg-[#1E3A2F] flex items-center justify-center disabled:opacity-50" data-testid="chat-send">
              <PaperPlaneTilt size={16} color="#fff" weight="fill" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
